/**
 * Currency formatting helpers (platform standard).
 *
 * Renders with en-GB + currencyDisplay:'symbol' so currencies are
 * country-disambiguated: AUD -> A$95.00, USD -> US$40.00, JPY -> JP¥1,200.
 *
 * Do NOT switch to narrowSymbol (collapses A$/US$ to a bare $) or en-AU.
 * The en-GB + 'symbol' combination is the only one that yields the
 * country-disambiguated A$ / US$ symbols this platform standard requires.
 * Confirmed empirically against V8 / Node 20.
 *
 * Two entry points by storage convention:
 *  - formatCurrency(amount, currency)         amount already in MAJOR units (dollars)
 *  - formatCurrencyFromMinor(amountMinor, ..)  amount in MINOR units (cents); divides
 *                                              by the currency's own decimal count.
 */
const LOCALE = 'en-GB';

/**
 * Format an amount already in MAJOR units (dollars).
 * @param {number} amount   Amount in major units.
 * @param {string} [currency='AUD']  ISO 4217 currency code.
 * @returns {string}
 */
export function formatCurrency(amount, currency) {
  const code = (currency || 'AUD').toUpperCase();
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`; // unknown/unsupported code fallback
  }
}

/**
 * Format an amount in MINOR units (e.g. cents). Divides by the currency's
 * own decimal count, so zero-decimal currencies (JPY) are handled correctly
 * and three-decimal (KWD) too — never a blanket /100.
 * @param {number} amountMinor  Amount in minor units (cents).
 * @param {string} [currency='AUD']  ISO 4217 currency code.
 * @returns {string}
 */
export function formatCurrencyFromMinor(amountMinor, currency) {
  const code = (currency || 'AUD').toUpperCase();
  const minor = Number(amountMinor) || 0;
  let fractionDigits = 2;
  try {
    fractionDigits = new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency: code,
    }).resolvedOptions().maximumFractionDigits;
  } catch {
    /* keep default 2 */
  }
  const major = minor / Math.pow(10, fractionDigits);
  return formatCurrency(major, code);
}
